import React, { useState, useRef } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { IoClose, IoCheckmark, IoPhonePortrait, IoTabletPortrait, IoDesktop } from 'react-icons/io5';

interface ImageCropModalProps {
  imageFile: File;
  onCropComplete: (croppedImageUrl: string) => void;
  onCancel: () => void;
}

type ViewMode = 'desktop' | 'tablet' | 'mobile';

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  imageFile,
  onCropComplete,
  onCancel,
}) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('desktop');
  const [imageSrc, setImageSrc] = useState<string>('');
  const imgRef = useRef<HTMLImageElement>(null);

  // Load image
  React.useEffect(() => {
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  const handleCropComplete = async () => {
    if (!completedCrop || !imgRef.current) {
      return;
    }

    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    
    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }
      const croppedUrl = URL.createObjectURL(blob);
      onCropComplete(croppedUrl);
    }, 'image/jpeg', 0.95);
  };

  const getPreviewSize = () => {
    switch (viewMode) {
      case 'desktop':
        return { width: '600px', height: '450px' };
      case 'tablet':
        return { width: '400px', height: '500px' };
      case 'mobile':
        return { width: '280px', height: '400px' };
      default:
        return { width: '600px', height: '450px' };
    }
  };

  const previewSize = getPreviewSize();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[110]">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-2xl">
          <h3 className="text-2xl font-bold text-gray-800">Crop Image</h3>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <IoClose size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* View Mode Selector */}
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setViewMode('desktop')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                viewMode === 'desktop'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <IoDesktop size={20} />
              Desktop
            </button>
            <button
              onClick={() => setViewMode('tablet')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                viewMode === 'tablet'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <IoTabletPortrait size={20} />
              Tablet
            </button>
            <button
              onClick={() => setViewMode('mobile')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                viewMode === 'mobile'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <IoPhonePortrait size={20} />
              Mobile
            </button>
          </div>

          {/* Crop Area */}
          <div className="flex justify-center">
            <div className="bg-gray-100 p-4 rounded-xl">
              {imageSrc && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={undefined}
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop preview"
                    className="max-h-96 object-contain"
                  />
                </ReactCrop>
              )}
            </div>
          </div>

          {/* Preview Section */}
          {completedCrop && (
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-800 text-center">
                Preview ({viewMode.charAt(0).toUpperCase() + viewMode.slice(1)} View)
              </h4>
              <div className="flex justify-center">
                <div
                  className="border-4 border-blue-500 rounded-lg overflow-hidden shadow-lg"
                  style={previewSize}
                >
                  <canvas
                    ref={(canvas) => {
                      if (!canvas || !completedCrop || !imgRef.current) return;

                      const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
                      const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
                      const ctx = canvas.getContext('2d');

                      if (!ctx) return;

                      canvas.width = completedCrop.width;
                      canvas.height = completedCrop.height;

                      ctx.drawImage(
                        imgRef.current,
                        completedCrop.x * scaleX,
                        completedCrop.y * scaleY,
                        completedCrop.width * scaleX,
                        completedCrop.height * scaleY,
                        0,
                        0,
                        completedCrop.width,
                        completedCrop.height
                      );
                    }}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleCropComplete}
              disabled={!completedCrop}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <IoCheckmark size={20} />
              Apply Crop
            </button>
            <button
              onClick={onCancel}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors"
            >
              <IoClose size={20} />
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
